import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon, type IconName } from '../Icon';
import { SkeletonSpinner } from '../SkeletonLoader';
import { Tooltip } from '../Tooltip';

const buttonVariants = cva(
  'font-noto inline-flex items-center justify-center whitespace-nowrap rounded-[6px] font-medium gap-1 text-xs leading-5 transition-all cursor-pointer disabled:cursor-not-allowed shrink-0 [&_svg]:shrink-0 focus-ring',
  {
    variants: {
      variant: {
        // Hollow: white bg, no border, dark text
        hollow: 'bg-transparent text-primary-dark hover:bg-grey-20 border border-transparent',
        // Secondary: grey-10 bg, grey-30 border, dark text
        secondary: 'bg-grey-10 border border-grey-30 text-primary-dark hover:bg-grey-30',
        // Primary: blue-80 bg, white text
        primary: 'bg-primary text-white border border-transparent hover:bg-primary-dark',
        // Discovery: blue-50 bg, white text
        discovery: 'bg-blue-50 text-white border border-transparent hover:bg-blue-60',
        // Success: green-50 bg, white text
        success: 'bg-green-50 text-white border border-transparent hover:bg-green-60',
        // Danger: grey-10 bg, grey-30 border, red text
        danger: 'bg-grey-10 border border-grey-30 text-red-50 font-semibold hover:bg-red-60 hover:text-white',
        // Disabled: grey-20 bg, grey-50 text
        disabled: 'bg-grey-20 text-grey-50 border border-transparent cursor-not-allowed hover:bg-grey-20',
        // Selected: blue-10 bg, blue-50 border, blue-60 text
        selected: 'bg-blue-10 border border-blue-50 text-blue-60 hover:bg-blue-20',
      },
      size: {
        small: 'px-2 py-0.5',
        standard: 'px-2 py-1',
        big: 'px-3 py-2',
        icon: 'p-1',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'standard',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    tooltip?: React.ReactNode;
    loading?: boolean;
    loadingSpinnerSize?: number;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    disabled,
    loading = false,
    loadingSpinnerSize = 14,
    tooltip,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  // Override variant to 'disabled' when disabled prop is true (but not when only loading - keep variant for loading state)
  const effectiveVariant = disabled && !loading ? 'disabled' : variant;
  const isDisabled = disabled || loading;

  const buttonElement = (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant: effectiveVariant, size, className }))}
      disabled={isDisabled}
      aria-busy={loading ? true : undefined}
      {...props}
    >
      {loading ? (
        <span className="relative inline-flex items-center justify-center">
          <span className="invisible inline-flex items-center gap-1">{children}</span>
          <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
            <SkeletonSpinner size={loadingSpinnerSize} />
          </span>
        </span>
      ) : (
        children
      )}
    </Comp>
  );

  // Only wrap with tooltip if disabled (or loading) and tooltip is provided
  if (isDisabled && tooltip) {
    const tooltipProps = typeof tooltip === 'string' ? { text: tooltip } : { contentChildren: tooltip };

    // Wrap in a focusable span to enable pointer events and keyboard focus for tooltip on disabled buttons
    return (
      <Tooltip {...tooltipProps} asChild>
        <span
          ref={ref}
          className="inline-flex rounded-[6px] focus-ring"
          tabIndex={0}
          role="button"
          aria-disabled="true"
          aria-label={props['aria-label']}
        >
          {buttonElement}
        </span>
      </Tooltip>
    );
  }

  return buttonElement;
});

export type IconButtonNameProps =
  | { children: Exclude<React.ReactNode, null | undefined | boolean>; 'aria-label'?: string }
  | { children?: undefined; 'aria-label': string };

export type IconButtonProps = Omit<React.ComponentProps<typeof Button>, 'children' | 'aria-label'> &
  VariantProps<typeof buttonVariants> & { icon: IconName; iconSize?: number } & IconButtonNameProps;

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, iconSize = 14, children, loading, ...props },
  ref,
) {
  return (
    <Button ref={ref} {...props} loading={loading} loadingSpinnerSize={iconSize}>
      <Icon icon={icon} size={iconSize} /> {children}
    </Button>
  );
});

export { Button, buttonVariants, IconButton };
